/**
 * Formateurs endpoints
 */

import { ENTITE_REFERENCE } from '../../utils/alertis-societes.js'
export const formateursEndpoints = {
  register: () => '/api/public/formateurs',
  verifyEmail: () => '/api/public/formateurs/verify-email',
  resendVerification: () => '/api/public/formateurs/resend-verification',
  // Inscription commencée puis interrompue : renvoi du lien de reprise par email.
  reprendreInscription: () => '/api/public/formateurs/reprendre-inscription',
  // Désinscription depuis le rappel mensuel : GET lit, POST met la fiche à la corbeille.
  desinscription: () => '/api/public/formateurs/desinscription',
  // Tous les envois automatiques vers les formateurs, pour la fenêtre de la liste.
  rappelsAutomatiques: () => '/api/formateurs/rappels-automatiques',
  // Les inscriptions de formateurs, jour après jour (commencées / terminées).
  statistiquesInscriptions: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return `/api/formateurs/statistiques-inscriptions${qs}`
  },
  // Passerelle : l'adresse appartient déjà à un compte de l'autre réseau. Le mot
  // de passe est vérifié ici, la session s'ouvre à l'arrivée (`continuerUrl`).
  passerelle: () => '/api/public/formateurs/passerelle',
  // Préremplissage du formulaire depuis un lien d'invitation (campagne MAJ).
  invitationPrefill: (token) => `/api/public/formateurs/invitation?token=${encodeURIComponent(token)}`,
  // Renouvellement annuel du contrat de sous-traitance : tout passe par le jeton
  // reçu par email, hors du portail — le formateur n'a pas à retrouver un mot de
  // passe pour signer une pièce d'une minute.
  contratAnnuelPublic: {
    etat: (token) => `/api/public/formateurs/contrat-annuel?token=${encodeURIComponent(token)}`,
    document: (token, entite = ENTITE_REFERENCE) =>
      `/api/public/formateurs/contrat-annuel/document?token=${encodeURIComponent(token)}&entite=${entite}`,
    signer: () => '/api/public/formateurs/contrat-annuel/signer',
    profil: (token) => `/api/public/formateurs/contrat-annuel/profil?token=${encodeURIComponent(token)}`,
    enregistrerProfil: () => '/api/public/formateurs/contrat-annuel/profil',
  },
  // Import Excel (admin) — aperçu (dry-run) puis exécution.
  import: {
    preview: () => '/api/formateur-import/preview',
    execute: () => '/api/formateur-import/execute',
  },
  // Campagne email de masse (admin) — statut, fiches à joindre, test, salves.
  campagne: {
    status: () => '/api/formateur-campagne/status',
    // Les fiches encore « invitées » avec leur situation vis-à-vis de la
    // campagne : la liste générique des formateurs ne sait pas la dire.
    invites: (params) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : ''
      return `/api/formateur-campagne/invites${qs}`
    },
    // L'évolution de la campagne : envois et réponses jour par jour, taux de
    // réponse, délai médian, répartition des états, bilan des salves. Séparé du
    // statut parce qu'il ne se lit pas au même rythme : le statut bat toutes
    // les quinze secondes pendant une salve, une courbe de trente jours non.
    stats: (params) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : ''
      return `/api/formateur-campagne/stats${qs}`
    },
    test: () => '/api/formateur-campagne/test',
    salve: () => '/api/formateur-campagne/salve',
    // La file des salves : avancement, salves programmées et bornes techniques
    // (débit tenable, plafond sur 24 h) — lues ensemble, jamais séparément.
    queue: () => '/api/formateur-campagne/queue',
    annulerSalve: (id) => `/api/formateur-campagne/queue/${id}`,
    // Relances automatiques : où elles en sont, et un passage à la demande.
    relances: () => '/api/formateur-campagne/relances',
    // Les deux registres que la campagne produit : les anciens formateurs
    // (inactifs) et ceux qu'aucune relance n'a fait réagir.
    inactifs: (params) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : ''
      return `/api/formateur-campagne/inactifs${qs}`
    },
    sansReponse: (params) => {
      const qs = params ? '?' + new URLSearchParams(params).toString() : ''
      return `/api/formateur-campagne/sans-reponse${qs}`
    },
  },
  // Actif / Inactif — un formateur inactif reste consultable mais ne reçoit
  // plus aucun email d'inscription ni de relance.
  // Communications e-mail / SMS : GET lit le statut, PUT { autorisees } le change.
  communications: (id) => `/api/formateurs/${id}/communications`,
  desactiver: (id) => `/api/formateurs/${id}/desactiver`,
  reactiver: (id) => `/api/formateurs/${id}/reactiver`,
  relancerInscription: (id) => `/api/formateurs/${id}/relancer-inscription`,
  derniereRelanceInscription: (id) => `/api/formateurs/${id}/derniere-relance-inscription`,
  inviterMiseAJour: (id) => `/api/formateurs/${id}/inviter-mise-a-jour`,
  derniereInvitationMiseAJour: (id) => `/api/formateurs/${id}/derniere-invitation-mise-a-jour`,
  activiteHistorique: (id) => `/api/formateurs/${id}/activite-historique`,
  missions: (id) => `/api/formateurs/${id}/missions`,
  list: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return `/api/formateurs${qs}`
  },
  // La carte de la liste : les mêmes filtres que `list`, mais sans page ni
  // limite — une carte montre tout le monde ou ne montre rien de vrai.
  carte: (params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return `/api/formateurs/carte${qs}`
  },
  // Le point de référence de la carte : ce que l'équipe a tapé (adresse, code
  // postal ou département) résolu en coordonnées côté serveur, par les mêmes
  // utilitaires que le lieu d'une mission.
  cartePoint: (q) => `/api/formateurs/carte/point?q=${encodeURIComponent(q)}`,
  detail: (id) => `/api/formateurs/${id}`,
  update: (id) => `/api/formateurs/${id}`,
  delete: (id) => `/api/formateurs/${id}`,
  restore: (id) => `/api/formateurs/${id}/restore`,
  // Efface l'identité et les pièces d'une fiche à la corbeille, garde ses
  // missions. Irréversible : la fiche ne désigne plus personne après.
  anonymiser: (id) => `/api/formateurs/${id}/anonymiser`,
  // Une fiche à la corbeille qui n'a jamais travaillé avec nous (spam, test)
  // se supprime pour de bon ; les autres s'anonymisent.
  etatSuppressionDefinitive: (id) => `/api/formateurs/${id}/suppression-definitive`,
  supprimerDefinitivement: (id) => `/api/formateurs/${id}/definitif`,
  resetPassword: (id) => `/api/formateurs/${id}/reset-password`,
  automaticEmails: {
    list: (id) => `/api/formateurs/${id}/automatic-emails`,
  },
  publicDocuments: {
    upload: (id) => `/api/public/formateurs/${id}/documents`,
    list: (id) => `/api/public/formateurs/${id}/documents`,
    update: (id, docId) => `/api/public/formateurs/${id}/documents/${docId}`,
    delete: (id, docId) => `/api/public/formateurs/${id}/documents/${docId}`,
  },
  calendar: (id, params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : ''
    return `/api/formateurs/${id}/calendar${qs}`
  },
  documents: {
    list: (id) => `/api/formateurs/${id}/documents`,
    upload: (id) => `/api/formateurs/${id}/documents`,
    download: (id, docId) => `/api/formateurs/${id}/documents/${docId}/download`,
    view: (id, docId) => `/api/formateurs/${id}/documents/${docId}/download?disposition=inline`,
    update: (id, docId) => `/api/formateurs/${id}/documents/${docId}`,
    delete: (id, docId) => `/api/formateurs/${id}/documents/${docId}`,
  },
  // Les pièces que le formateur signe à la fin de son inscription. Le contrat
  // annuel de sous-traitance EST le contrat cadre : un seul document, dont on a
  // seulement avancé le moment de naissance. Il y en a QUATRE et non deux —
  // Alertis est deux sociétés, et le formateur s'engage envers chacune : d'où
  // le paramètre `entite` sur chaque téléchargement.
  chartes: {
    list: (id) => `/api/formateurs/${id}/chartes`,
  },
  charte: {
    download: (id, entite = ENTITE_REFERENCE) =>
      `/api/formateurs/${id}/charte/download?entite=${entite}`,
    generate: (id) => `/api/formateurs/${id}/charte/generate`,
  },
  contratCadre: {
    download: (id) => `/api/formateurs/${id}/contrat-cadre/download`,
    generate: (id) => `/api/formateurs/${id}/contrat-cadre/generate`,
  },
  // L'historique des contrats annuels : un document par année, conservé après
  // son échéance. La fiche du back-office les donne à lire, elle n'en fabrique
  // aucun — ils naissent à l'inscription puis au renouvellement du 1er janvier.
  contratsAnnuels: {
    list: (id) => `/api/formateurs/${id}/contrats-annuels`,
    download: (id, annee, entite = ENTITE_REFERENCE) =>
      `/api/formateurs/${id}/contrats-annuels/${annee}/download?entite=${entite}`,
  },
  blacklister: (id) => `/api/formateurs/${id}/blacklister`,
  deblacklister: (id) => `/api/formateurs/${id}/deblacklister`,
  duplicates: (id) => `/api/formateurs/${id}/duplicates`,
  experiences: {
    add: (id) => `/api/formateurs/${id}/experiences`,
    update: (id, experienceId) => `/api/formateurs/${id}/experiences/${experienceId}`,
    delete: (id, experienceId) => `/api/formateurs/${id}/experiences/${experienceId}`,
  },
  rechercheClientsLiables: (q) => `/api/formateurs/recherche-clients-liables?${new URLSearchParams({ q })}`,
  experiencesParClient: (email) => `/api/formateurs/experiences-par-client?${new URLSearchParams({ email })}`,
  rechercheLiables: (q, exclure) =>
    `/api/formateurs/recherche-liables?${new URLSearchParams({ q, ...(exclure ? { exclure } : {}) })}`,
  cartons: {
    add: (id) => `/api/formateurs/${id}/cartons`,
    delete: (id, cartonId) => `/api/formateurs/${id}/cartons/${cartonId}`,
  },
}
